import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import SectionDependencyRow from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/DocumentSettings/Sections/SplitSection/SectionDependencyRow';
import type DocumentSettingsFormData from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/DocumentSettingsFormData';
import CheckboxTile from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Shared/CheckboxTile';
import HintText from '@Guidelines/Component/HintText';
import SectionCard from '@Guidelines/Component/SectionCard';
import type DocumentTypeSectionsView from '@UI/Store/OrganizationWorkflows/Views/DocumentTypeSectionsView';
import { css } from '@emotion/react';
import * as React from 'react';
import { Button, Message, Skeleton } from '@verclam/ui-kit';

const tableStyle = css`
	display: flex;
	flex-direction: column;
	padding: 0.25rem 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

const tableHeaderStyle = css`
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
	gap: 0.75rem;
	padding: 0.75rem 0;
	border-bottom: 1px solid ${theme.colors.borderStrong};
	font-family: ${theme.fonts.mono};
	font-size: 0.65625rem;
	letter-spacing: 0.12em;
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly settings: DocumentSettingsFormData;
	/** Null until loaded for this document type. */
	readonly sections: DocumentTypeSectionsView | null;
	readonly sectionsError: Error | null;
	/** Dependencies dropped on opening because the template no longer has their section. */
	readonly removedDependencyCount: number;
	readonly onRetry: () => void;
	readonly onChange: React.Dispatch<React.SetStateAction<DocumentSettingsFormData>>;
};

/**
 * Whether the document is written section by section and, if so, which sections wait for which
 * others. The sections come from the organization's template for the document type; without a
 * template the dependencies already set are kept untouched.
 */
const SplitSection: React.FC<Props> = ({
	settings,
	sections,
	sectionsError,
	removedDependencyCount,
	onRetry,
	onChange,
}) => {
	const [editedPath, setEditedPath] = React.useState<string | null>(null);

	const handleSplitChange = React.useCallback(
		(checked: boolean) => {
			onChange((current) => current.setSplitCalls(checked));
		},
		[onChange],
	);

	const renderDependencies = (): React.ReactNode => {
		if (sections === null && sectionsError === null) {
			return <Skeleton height="6rem" borderRadius={theme.radii.medium} animation="wave" />;
		}

		if (sections === null) {
			return (
				<React.Fragment>
					<HintText>Les sections du gabarit n’ont pas pu être chargées.</HintText>
					<div>
						<Button
							type="button"
							size="sm"
							severity="secondary"
							label="Réessayer"
							onClick={onRetry}
						/>
					</div>
				</React.Fragment>
			);
		}

		if (!sections.hasTemplate) {
			return (
				<HintText>
					Aucun gabarit n’est affecté à ce type pour la structure : les dépendances entre sections
					ne peuvent pas être réglées.
				</HintText>
			);
		}

		return (
			<React.Fragment>
				<ConditionalLoader data={removedDependencyCount > 0 ? removedDependencyCount : null}>
					{(count) => (
						<Message
							severity="warn"
							text={
								count === 1
									? '1 dépendance vers une section absente du gabarit a été retirée. Vérifiez le découpage, puis enregistrez.'
									: `${count} dépendances vers des sections absentes du gabarit ont été retirées. Vérifiez le découpage, puis enregistrez.`
							}
						/>
					)}
				</ConditionalLoader>
				<div css={tableStyle}>
					<div css={tableHeaderStyle}>
						<span>Section</span>
						<span>Rédigée après</span>
					</div>
					{sections.sections.map((section) => (
						<SectionDependencyRow
							key={section.path || 'general'}
							section={section}
							sections={sections.sections}
							settings={settings}
							isEdited={editedPath === section.path}
							onEditedPathChange={setEditedPath}
							onChange={onChange}
						/>
					))}
				</div>
			</React.Fragment>
		);
	};

	return (
		<SectionCard title="Découpage">
			<CheckboxTile
				name="splitCalls"
				label="Générer section par section"
				checked={settings.splitCalls}
				onChange={handleSplitChange}
			/>
			<HintText>Chaque section est rédigée à part, après celles dont elle a besoin.</HintText>
			<ConditionalLoader data={settings.splitCalls}>{renderDependencies}</ConditionalLoader>
		</SectionCard>
	);
};

export default SplitSection;
